import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import PageNav from "../components/site/PageNav";
import Footer from "../components/site/Footer";

const EASE = [0.76, 0, 0.24, 1];

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#030712] min-h-screen" data-testid="blog-page">
      <Helmet>
        <title>Insights & Perspectives — Yes Global Consultants</title>
        <meta name="description" content="Insights on executive search, HR strategy, leadership hiring and talent architecture from Yes Global Consultants." />
        <link rel="canonical" href="https://yesconsultants.in/blog" />
        <meta property="og:title" content="Insights & Perspectives — Yes Global Consultants" />
        <meta property="og:description" content="Thought leadership on executive search & HR consulting." />
        <meta property="og:url" content="https://yesconsultants.in/blog" />
      </Helmet>
      <PageNav />

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-24">
        <div className="max-w-2xl mb-16">
          <span className="font-body text-xs tracking-[0.35em] text-[#00D166]">INSIGHTS</span>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold tracking-tight text-white">
            Perspectives on <span className="font-serifed italic font-normal text-white/60">leadership.</span>
          </h1>
          <p className="mt-6 font-body text-white/55 leading-relaxed">
            Field notes on executive search, HR strategy and the art of building elite teams.
          </p>
        </div>

        {loading ? (
          <p className="font-body text-white/50">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="border border-white/10 p-16 text-center" data-testid="blog-empty">
            <p className="font-serifed italic text-2xl text-white/60">No insights published yet.</p>
            <p className="mt-2 font-body text-sm text-white/40">Check back soon for thought leadership from our team.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col h-full border border-white/10 hover:border-[#00D166]/40 bg-[#050B14] transition-colors duration-400"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#0F172A]">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="h-full w-full grid place-items-center">
                        <span className="font-display text-4xl font-extrabold text-white/5">YG</span>
                      </div>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="font-body text-[10px] tracking-wide uppercase text-[#00D166] border border-[#00D166]/30 px-2 py-1">{t}</span>
                      ))}
                    </div>
                    <h2 className="font-display text-xl font-bold text-white leading-snug group-hover:text-[#00D166] transition-colors">{p.title}</h2>
                    <p className="mt-3 font-body text-sm text-white/55 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
                      <span className="font-body text-xs text-white/40">{fmtDate(p.created_at)}</span>
                      <ArrowUpRight size={16} className="text-white/40 group-hover:text-[#00D166] transition-colors" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
